import { useMemo, useState } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown, CheckCircle2, Clock } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useVisitadoresAnalyticsDifare } from '../hooks/useAnalyticsDifare'
import type { VisitadorAnalyticsDifare } from '../types'
import { nf, toTitleCase } from '../format'

type SortKey =
  | 'nombre'
  | 'dependientes_registrados'
  | 'trivias_completadas'
  | 'pct_completados'

const COLUMNAS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: 'nombre', label: 'Visitador', numeric: false },
  { key: 'dependientes_registrados', label: 'Dependientes', numeric: true },
  { key: 'trivias_completadas', label: 'Trivias completadas', numeric: true },
  { key: 'pct_completados', label: '% completados', numeric: true },
]

// % de dependientes registrados por el visitador que completaron la trivia
const pctCompletados = (v: VisitadorAnalyticsDifare) =>
  v.dependientes_registrados > 0
    ? (v.trivias_completadas / v.dependientes_registrados) * 100
    : 0

export function VisitadoresTab() {
  const { data, isLoading, error } = useVisitadoresAnalyticsDifare()
  const [sortKey, setSortKey] = useState<SortKey>('trivias_completadas')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const filas = useMemo(() => {
    const items = [...(data ?? [])]
    const valueOf = (v: VisitadorAnalyticsDifare) =>
      sortKey === 'pct_completados' ? pctCompletados(v) : v[sortKey]
    items.sort((a, b) => {
      const av = valueOf(a)
      const bv = valueOf(b)
      let cmp: number
      if (typeof av === 'number' && typeof bv === 'number') {
        cmp = av - bv
      } else {
        cmp = String(av ?? '').localeCompare(String(bv ?? ''), 'es')
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
    return items
  }, [data, sortKey, sortDir])

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center py-32">
        <p className="text-destructive text-sm text-center max-w-md">
          No se pudo cargar el reporte de visitadores. Verifique la configuración e intente de nuevo.
        </p>
      </div>
    )
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700">
      <CardHeader>
        <CardTitle className="text-sm font-medium">Visitadores ({nf(data.length)})</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-100 dark:bg-[hsl(220,15%,16%)] hover:bg-gray-100 dark:hover:bg-[hsl(220,15%,16%)]">
                {COLUMNAS.map((col) => {
                  const active = col.key === sortKey
                  const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown
                  return (
                    <TableHead key={col.key} className={col.numeric ? 'text-right' : undefined}>
                      <button
                        type="button"
                        onClick={() => toggleSort(col.key)}
                        className={`inline-flex items-center gap-1 hover:text-foreground ${
                          col.numeric ? 'flex-row-reverse' : ''
                        } ${active ? 'text-foreground font-semibold' : ''}`}
                      >
                        {col.label}
                        <Icon className="h-3 w-3 opacity-60" />
                      </button>
                    </TableHead>
                  )
                })}
                <TableHead className="text-center w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas.length > 0 ? (
                filas.map((v) => (
                  <TableRow key={v.visitador_id}>
                    <TableCell className="font-medium">{toTitleCase(v.nombre) || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {nf(v.dependientes_registrados)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {nf(v.trivias_completadas)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {pctCompletados(v).toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-center">
                      {pctCompletados(v) >= 100 ? (
                        <CheckCircle2
                          className="inline h-4 w-4 text-emerald-500/70"
                          aria-label="100% completado"
                        />
                      ) : (
                        <Clock
                          className="inline h-4 w-4 text-muted-foreground/50"
                          aria-label="Pendiente"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-10">
                    Sin visitadores registrados.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}
